import { AuthenticationResult, Environment } from '@qvs/core';
import { Injectable } from '@angular/core';
import { from, Observable, of, repeat } from 'rxjs';
import { catchError, exhaustMap, filter, finalize, map, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import * as fromActions from '@qvs/core/auth';
import { authenticateByContractNumberFailure, cancelAuthentication } from '@qvs/core/auth';
import { Store } from '@ngrx/store';
import * as authenticationApi from '@authentication-api';
import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { IAuthenticatorService } from '~/shared/services/IAuthenticatorService';
import { CapacitorCookies } from '@capacitor/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthenticatorService implements IAuthenticatorService {
  private readonly maxStatusChecksBeforeTimeout = 60;
  private readonly statusCheckInterval = 500;

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly nevisService: authenticationApi.NevisService,
    private readonly router: Router
  ) {}

  private static handleStatusResponse(
    response: HttpResponse<authenticationApi.NevisStatusResponse>,
    authenticationResult: AuthenticationResult
  ): Observable<AuthenticationResult> {
    if (response.status !== HttpStatusCode.Ok) {
      return of({
        isServerError: true,
        isCanceled: true
      });
    }
    const data = response.body;
    return of({
      ...authenticationResult,
      chatToken: data.chatToken,
      isAuthenticated: data.chatToken && data.status === authenticationApi.NevisStatusResponse.StatusEnum.SUCCEEDED,
      isCanceled: data.status === authenticationApi.NevisStatusResponse.StatusEnum.FAILED,
      isPending: data.status === authenticationApi.NevisStatusResponse.StatusEnum.PENDING
    });
  }

  authenticate(contractNumber: string, _environment: Environment): Observable<AuthenticationResult> {
    return this.requestAuthentication(contractNumber, 'app');
  }

  logout(authenticationResult: AuthenticationResult): Observable<void> {
    return this.nevisService.logout({ statusToken: authenticationResult.statusToken }).pipe(
      map(() => undefined) // to ensure that the Observable emits a value
    );
  }

  private clearAllCookies(): Promise<void> {
    // TODO 09-Jun-2026/braune: check implementation, or whether to replace with document.cookie / cookieStore API
    //  check also if HttpOnly flag is set
    return CapacitorCookies.clearAllCookies();
  }

  private requestAuthentication(contractNumber: string, mode: 'app' | 'push'): Observable<AuthenticationResult> {
    const canceled$ = this.actions$.pipe(ofType(cancelAuthentication));

    return from(this.clearAllCookies()).pipe(
      switchMap(() =>
        this.nevisService.authenticate(
          {
            contractnumber: contractNumber,
            mode
          },
          'response'
        )
      ),
      catchError((error) => {
        this.store.dispatch(authenticateByContractNumberFailure({ authenticationResult: null, error }));
        throw error;
      }),
      map((response): AuthenticationResult => ({
        statusToken: response.body.statusToken,
        qrCode: { dataUri: response.body.qrCode.dataUri },
        isAuthenticated: false
      })),
      filter((authenticationResult) => !!authenticationResult?.statusToken && !!authenticationResult?.qrCode),
      takeUntil(canceled$),
      take(1),
      tap((authenticationResult) => this.store.dispatch(fromActions.createAuthenticationResultSuccess({ authenticationResult }))),
      // TODO 10-Jun-2026/braune: move to ebanking app and reuse loginRoutePaths
      tap(() => this.router.navigateByUrl('/scan')),
      switchMap((authenticationResult) => this.pollAuthenticationStatus(authenticationResult)),
      filter(
        (authenticationResult) =>
          authenticationResult.isAuthenticated || authenticationResult.isCanceled || authenticationResult.isTimedOut
      ),
      takeUntil(canceled$),
      take(1),
      filter((authenticationResult) => authenticationResult.isAuthenticated),
      // TODO 10-Jun-2026/braune: move to ebanking app and reuse loginRoutePaths
      tap(() => this.router.navigateByUrl('/pages'))
    );
  }

  private readonly pollAuthenticationStatus = (authenticationResult: AuthenticationResult): Observable<AuthenticationResult> => {
    // store last result of polling to check if it was canceled in finalize
    let lastResult: AuthenticationResult;

    return this.nevisService
      .status(
        {
          statusToken: authenticationResult.statusToken,
          appStatus: authenticationApi.NevisStatusRequest.AppStatusEnum.NA
        },
        'response'
      )
      .pipe(
        exhaustMap((response) => AuthenticatorService.handleStatusResponse(response, authenticationResult)),
        tap((result) => (lastResult = result)),
        takeWhile((authResult: AuthenticationResult) => !authResult.isPending, true),
        repeat({ count: this.maxStatusChecksBeforeTimeout, delay: this.statusCheckInterval }),
        tap({
          error: () => of({ isServerError: true, isCanceled: true }),
          complete: () => of({ isTimedOut: true, isCanceled: true })
        }),
        finalize(() => {
          // isCanceled --> canceled in access app, isPending --> QR expired due to timeout
          if (lastResult?.isCanceled || lastResult?.isPending) {
            this.store.dispatch(
              fromActions.authenticateByContractNumberFailure({ authenticationResult: lastResult, error: null })
            );
          }
        })
      );
  };
}
